import { useCallback, useEffect } from 'react';
import { add, isAfter } from 'date-fns';
import Cookies from 'js-cookie';
import { useEffectOnce } from 'react-use';
import useEnablePricing from '@/enterprise/components/OfferBuilder/useEnablePricing';
import useEnableAutomations from '@/enterprise/components/useEnableAutomations';
import useEnableUnification from '@/enterprise/components/useEnableUnification';
import { NAVIGATION_CONFIG_KEY } from '@/enterprise/data/navigationArtifactSelectors';
import { useNavigationArtifact } from '@/enterprise/data/useArtifacts';
import useRecentAwards from '@/enterprise/data/useRecentAwards';
import useTakerMarkets, { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import useFeature from '@/lib/features';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import useUserDetails from '@/shared/data/useUserDetails';
import useAccountFeatureConfigs from '@/shared/providers/useAccountFeatureConfigs';
import { useToken } from '@/utils/token';
import { useReporting } from './Reporting';
import { useEffectiveUnificationDate } from './useEffectiveUnificationDate';
import { UNIFICATION_MIGRATION_45_DAY_THRESHOLD } from './utils';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Intercom: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    intercomSettings: any;
  }
}

function createScript() {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = 'https://widget.intercom.io/widget/dn0blqde';
  return script;
}

function getLastClearTime(markets: TakerMarket[]) {
  return markets.reduce<string | null>((latest, takerMarket) => {
    if (!takerMarket.lastClearDate) {
      return latest;
    }

    if (!latest || takerMarket.lastClearDate > latest) {
      return takerMarket.lastClearDate;
    }

    return latest;
  }, null);
}

const INTERCOM_APP_ID = import.meta.env.VITE_INTERCOM_APP_ID;
const SMB_MIGRATION_SURVEY_DELAY_DAYS = 14;
const NPS_REACTIVATION_DAYS = 45;

// See the tracking guide for more information on how to test locally.
export function useIntercom() {
  const { data: userDetails } = useUserDetails();
  const { trackEnterpriseEvent } = useReporting();
  const { data: recentAwards } = useRecentAwards();
  const hasParticipated = recentAwards.length > 0;
  const isUnified = useEnableUnification();
  const emailAddress = userDetails?.me?.emailAddress ?? '';
  const internalUserDomains = ['@c2fo.com', '@wfcfund.com', '@priorityvendor.com', '@synthetics.dtdg.co'];
  const isC2foUser = internalUserDomains.find((host) => emailAddress.endsWith(host));
  const effectiveUnificationDate = useEffectiveUnificationDate();

  // True when a unified account has passed 45 days since their effective unification date,
  // at which point NPS resumes normally.
  const isPastNpsReactivationDate =
    effectiveUnificationDate != null &&
    isAfter(new Date(), add(new Date(effectiveUnificationDate), { days: NPS_REACTIVATION_DAYS }));

  // Intercom will let us only show the survey every 90 days, but the cookie logic is reused here so the track event behavior
  // matches with Qualtrics
  const hasRecentlyCompletedNpsSurvey = Cookies.get('last_nps_run');

  useEffectOnce(() => {
    if (INTERCOM_APP_ID) {
      const script = createScript();
      document.head.appendChild(script);
    }
  });

  const { Intercom, intercomSettings } = window;

  useEffect(() => {
    if (typeof Intercom === 'function') {
      Intercom('reattach_activator');
      Intercom('update', intercomSettings);
    }
  }, [Intercom, intercomSettings]);

  const setIntercomSettings = useCallback(
    (settings: Record<string, unknown>) => {
      window.intercomSettings = settings;
      if (Intercom) {
        Intercom('update', settings);
      }
    },
    [Intercom]
  );

  const openChat = useCallback(() => {
    if (window.Intercom) {
      window.Intercom('show');
    }
  }, []);

  const triggerNPSSurvey = useCallback(
    (config?: { forceTrigger?: boolean }) => {
      const { forceTrigger = false } = config ?? {};

      // After 45 days post-unification (SMB migration or enterprise go-live), NPS resumes normally — skip the isUnified suppression.
      const suppressedByUnification = isUnified && !isPastNpsReactivationDate;

      if (
        !forceTrigger &&
        (isC2foUser || hasRecentlyCompletedNpsSurvey || !hasParticipated || suppressedByUnification)
      ) {
        trackEnterpriseEvent('user::nps:survey::skipped', {
          isC2foUser: !!isC2foUser,
          hasRecentlyCompletedNpsSurvey: !!hasRecentlyCompletedNpsSurvey,
          hasParticipated,
          suppressedByUnification,
          referrer: 'intercom',
        });
        return;
      }

      if (window.Intercom) {
        Cookies.set('last_nps_run', 'true', { expires: 90 });

        // Update user properties not directly captured by the survey and
        // Clear out any properties from previous survey runs
        // If no text is entered for the question/most important thing step, then Intercom will keep
        // the old value unless we do this
        window.Intercom('update', {
          nps_page: window.location.href,
          nps_comment: '',
          nps_most_important_thing_to_improve: '',
          nps_language: userDetails?.me?.personalInfo?.locale ?? '',
          nps_last_survey_date: new Date().toISOString(),
        });

        // Send the event to start the survey, this will trigger the series to run on Intercom's side
        // Intercom will handle language selection and only showing the survey every 90 days
        window.Intercom('trackEvent', 'start-nps-survey');

        trackEnterpriseEvent('user::nps:survey::shown', {
          referrer: 'intercom',
        });
      }
    },
    [
      hasParticipated,
      hasRecentlyCompletedNpsSurvey,
      isC2foUser,
      isUnified,
      trackEnterpriseEvent,
      isPastNpsReactivationDate,
      userDetails?.me?.personalInfo?.locale,
    ]
  );

  return {
    Intercom,
    intercomSettings,
    setIntercomSettings,
    openChat,
    triggerNPSSurvey,
  };
}

function IntercomComponent() {
  const { tokenContent } = useToken();
  const { setIntercomSettings } = useIntercom();
  const { data: user } = useUserDetails(({ me }) => ({
    name: `${me?.personalInfo?.firstName} ${me?.personalInfo?.lastName}`,
    email: me?.emailAddress,
    created: me?.created,
    userHash: me?.intercomUserId,
    loginHistory: me?.loginHistory,
    accountId: me?.account?.id,
  }));
  const { wasSmbMigrated } = useAccountFeatureConfigs();
  const isUnified = useEnableUnification();
  const hasPricing = useEnablePricing();
  const hasAutomations = useEnableAutomations();
  const [fundboxEnabled] = useFeature('enterprise-ui_enableFundbox');
  const { data: fundboxNavigationArtifact } = useNavigationArtifact(NAVIGATION_CONFIG_KEY.FUNDBOX);
  const hasFundbox = Boolean(fundboxEnabled && fundboxNavigationArtifact);
  const effectiveUnificationDate = useEffectiveUnificationDate();
  const {
    data: { lastClearTime, hasActiveOfferNonClearingInvoices },
  } = useTakerMarkets((markets) => ({
    lastClearTime: getLastClearTime(markets),
    hasActiveOfferNonClearingInvoices: markets.some(
      (takerMarket) => takerMarket.offerConfig.isEnabled === true && takerMarket.notAcceptedInvoiceAmount > 0
    ),
  }));

  const authServiceUuid = tokenContent?.payload?.user?.uuid ?? '';

  // True when the account has cleared at least one offer since their unification date (SMB migration or enterprise go-live)
  const clearedAfterSmbMigration =
    effectiveUnificationDate != null && lastClearTime != null && lastClearTime >= effectiveUnificationDate;

  // The earliest date from which post-unification Intercom surveys are eligible to trigger (unification date + 14 days).
  const smbMigrationSurveyEligibleDate =
    effectiveUnificationDate != null
      ? add(new Date(effectiveUnificationDate), { days: SMB_MIGRATION_SURVEY_DELAY_DAYS })
      : null;

  // True when the post-unification survey delay has passed (unification date + 14 days ago).
  const smbMigrationOver14DaysAgo =
    smbMigrationSurveyEligibleDate != null && isAfter(new Date(), smbMigrationSurveyEligibleDate);

  // True when 45 days have passed since the unification date (aligns with NPS reactivation threshold).
  const smbMigrationOver45DaysAgo =
    effectiveUnificationDate != null &&
    isAfter(new Date(), add(new Date(effectiveUnificationDate), { days: UNIFICATION_MIGRATION_45_DAY_THRESHOLD }));

  // True when the user has logged in at least twice since the post-unification survey delay passed.
  const loginsAfterSmbMigrationSurveyDelay = smbMigrationSurveyEligibleDate
    ? (user?.loginHistory ?? []).filter(
        (login) => login?.created != null && isAfter(new Date(login.created), smbMigrationSurveyEligibleDate)
      )
    : [];
  const hasTwoLoginsAfterSmbMigrationSurveyDelay = loginsAfterSmbMigrationSurveyDelay.length >= 2;

  useEffect(() => {
    if (user) {
      const { email, userHash } = user;
      setIntercomSettings({
        api_base: 'https://api-iam.intercom.io',
        app_id: INTERCOM_APP_ID,
        email,
        user_id: authServiceUuid,
        user_hash: userHash,
        experience: import.meta.env.VITE_APP_NAME,
        was_smb_migrated: wasSmbMigrated,
        cleared_after_smb_migration: clearedAfterSmbMigration,
        has_active_offer_non_clearing_invoices: hasActiveOfferNonClearingInvoices,
        smb_migration_over_14_days_ago: smbMigrationOver14DaysAgo,
        smb_migration_over_45_days_ago: smbMigrationOver45DaysAgo,
        has_two_logins_after_smb_migration_survey_delay: hasTwoLoginsAfterSmbMigrationSurveyDelay,
        has_unified_experience: isUnified,
        has_automations: hasAutomations,
        has_pricing: hasPricing,
        has_fundbox: hasFundbox,
        alignment: 'left',
        hide_default_launcher: true,
      });
    }
  }, [
    user,
    authServiceUuid,
    isUnified,
    wasSmbMigrated,
    clearedAfterSmbMigration,
    hasActiveOfferNonClearingInvoices,
    smbMigrationOver14DaysAgo,
    smbMigrationOver45DaysAgo,
    hasTwoLoginsAfterSmbMigrationSurveyDelay,
    hasAutomations,
    hasPricing,
    hasFundbox,
    setIntercomSettings,
  ]);

  return null;
}

export default function Intercom() {
  return (
    <QueryBoundaries>
      <IntercomComponent />
    </QueryBoundaries>
  );
}
